import { useFormikContext } from 'formik'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Body1, Heading6 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { DocumentFileUpload } from '@ui/components/FileUpload/DocumentFileUpload'
import { FileData } from '@ui/components/FileUpload/types'

import {
  useCategorySizesReadContext,
  useCategorySizesWriteContext,
} from '@utils/CategorySizesContext'

import { DocumentCategoryType } from '@model/DocumentCategories'
import { WalletDto, isWallet } from '@model/WalletDto'

import { FieldGroup } from '@global/form/FieldGroup'
import { createFormField } from '@global/formField/FormField'
import { TransactionFormActionButtonTemplate } from '@global/templates/TransactionFormActionButtonTemplate'

import { FieldWrapper } from '@shared/components/FieldWrapper'

import { CurrencyAmountField } from './CurrencyAmountField'
import { PaymentAgentTransferFormValues } from './PaymentAgentTransferForm'

import styles from './PaymentAgentTransferClientDetailsFields.module.scss'

interface PaymentAgentTransferClientDetailsFieldsProps {
  onCancel(): void
}
interface PaymentAgentTransferClientDetailsFormValues {
  clientWallet: WalletDto
  paymentProviderWallet?: WalletDto
  campaignName: string
  system: string
  account: string
  comment: string
}

const FormField = createFormField<PaymentAgentTransferClientDetailsFormValues>()

export const PaymentAgentTransferClientDetailsFields: React.FC<
  PaymentAgentTransferClientDetailsFieldsProps
> = (props) => {
  const { onCancel } = props
  const { categorySizes } = useCategorySizesReadContext()
  const { refreshCategorySizes } = useCategorySizesWriteContext()

  useEffect(() => {
    refreshCategorySizes()
  }, [])

  const { values, isValid, setValues } = useFormikContext<PaymentAgentTransferFormValues>()

  const handleUploadDocumentField = (data?: FileData) => {
    if (!data) {
      setValues({
        ...values,
        fileName: '',
        fileData: undefined,
        base64Content: '',
      })
    } else {
      setValues({
        ...values,
        fileName: data.fileName,
        fileData: data,
        base64Content: data.base64Content,
      })
    }
  }

  const { t } = useTranslation()

  return (
    <React.Fragment>
      <FieldWrapper>
        <FormField name='clientWallet.name' label={t('Wallet.Client Wallet Name')} required />
        {isWallet(values.paymentProviderWallet) && (
          <FormField
            name='paymentProviderWallet.currency.id'
            label={t('Wallet.Transfer From')}
            value={
              values?.paymentProviderWallet?.currency &&
              `${values.paymentProviderWallet?.currency?.id} ${t('Wallet.Wallet')}`
            }
            hint={
              values?.paymentProviderWallet?.currency &&
              `${t('Wallet.Balance')} ${values.paymentProviderWallet.balance} ${
                values.paymentProviderWallet.currency?.id
              }`
            }
          />
        )}

        <FieldGroup>
          <CurrencyAmountField />
          <div className={styles.currencyFieldWrapper}>
            <FormField name='clientWallet.currency.id' label={t('Wallet.Currency')} disabled />
          </div>
        </FieldGroup>

        <div className='pt-4'>
          <div className='pb-4'>
            <Heading6 medium>{t('Wallet.Additional Transaction Details')}</Heading6>
          </div>
          <FieldWrapper>
            <FormField name='system' label={t("Wallet.Client's Bank or Payment System")} required />
            <FormField
              name='account'
              label={t("Wallet.Client's Bank or Payment System Account")}
              required
            />
            <FormField name='comment' label={t('Wallet.Additional Comment')} />
            <FormField name='campaignName' label={t('Trading Account.Trading Account')} />
          </FieldWrapper>
        </div>
        <div className='pt-4 pb-4'>
          <Body1 className={styles.wallet} medium>
            {t('Wallet.Proof of Payment')} *
          </Body1>
          <div>
            <DocumentFileUpload
              title={t('Wallet.Document front side:')}
              file={values.fileData}
              documentCategory={DocumentCategoryType.PaymentAgent}
              categorySizes={categorySizes}
              onUpload={handleUploadDocumentField}
              onRemove={() => handleUploadDocumentField(undefined)}
            />
          </div>
        </div>

        <TransactionFormActionButtonTemplate>
          <Button.Secondary size='lg' onClick={onCancel}>
            {t('Cancel')}
          </Button.Secondary>
          <Button.Primary size='lg' type='submit' disabled={!isValid}>
            {t('Confirm')}
          </Button.Primary>
        </TransactionFormActionButtonTemplate>
      </FieldWrapper>
    </React.Fragment>
  )
}
